/* 4ammer Zone membership page (sections/zone-membership.liquid).
   A custom element, so theme editor section reloads re-run connectedCallback
   and disconnectedCallback cleans up listeners and observers.
   Program data and the customer's balance come from the Smile.io storefront
   SDK (window.Smile); every read is optional and falls back to the values
   rendered by Liquid. */
(function () {
  if (customElements.get('zone-membership')) return;

  var SMILE_WAIT_MS = 15000;
  var SMILE_POLL_MS = 250;
  var SMILE_CALL_TIMEOUT_MS = 8000;
  var TIER_MILESTONES = [0, 3000, 10000];
  var TIER_NAMES = ['Morning Riser', 'Morning Lover', 'Morning Muse'];

  var nf = new Intl.NumberFormat('pt-BR');
  var fmtPoints = function (n) { return nf.format(n); };
  var fmtMoney = function (n) { return 'R$ ' + nf.format(n); };

  /* The Smile app embed defines window.loadSmileSdk after this deferred script
     may already have run, so keep asking for the SDK until the loader exists. */
  var requestedSdk = false;
  var requestSdk = function () {
    if (requestedSdk || typeof window.loadSmileSdk !== 'function') return;
    requestedSdk = true;
    try { window.loadSmileSdk(); } catch (e) { /* the SDK is optional */ }
  };

  var waitForSmile = function () {
    if (window.Smile) return Promise.resolve(window.Smile);
    requestSdk();
    return new Promise(function (resolve) {
      var waited = 0;
      var timer = setInterval(function () {
        waited += SMILE_POLL_MS;
        requestSdk();
        if (window.Smile || waited >= SMILE_WAIT_MS) {
          clearInterval(timer);
          resolve(window.Smile || null);
        }
      }, SMILE_POLL_MS);
    });
  };

  /* Resolve with null instead of hanging when a Smile call never settles. */
  var withTimeout = function (promise) {
    return Promise.race([
      Promise.resolve(promise).catch(function () { return null; }),
      new Promise(function (resolve) { setTimeout(function () { resolve(null); }, SMILE_CALL_TIMEOUT_MS); })
    ]);
  };

  var safeGet = function (resource) {
    if (!resource || typeof resource.get !== 'function') return Promise.resolve(null);
    try { return withTimeout(resource.get()); } catch (e) { return Promise.resolve(null); }
  };

  var firstNumber = function () {
    for (var i = 0; i < arguments.length; i++) {
      if (typeof arguments[i] === 'number' && isFinite(arguments[i])) return arguments[i];
    }
    return null;
  };

  class ZoneMembership extends HTMLElement {
    connectedCallback() {
      this.cleanups = [];
      this.step = parseInt(this.dataset.step, 10) || 1000;
      this.reward = parseInt(this.dataset.reward, 10) || 50;
      this.milestones = TIER_MILESTONES.slice();
      this.balance = null;
      this.setupSimulator();
      this.setupSmileButtons();
      this.setupAnchor();
      this.setupTierDots();
      if (this.dataset.loggedIn === 'true') this.watchSmileUiBalance();
      this.loadSmileData();
    }

    disconnectedCallback() {
      (this.cleanups || []).forEach(function (fn) { fn(); });
      this.cleanups = [];
    }

    on(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.cleanups.push(function () { target.removeEventListener(type, handler, options); });
    }

    /* ---------- Smile data ---------- */

    loadSmileData() {
      var self = this;
      waitForSmile().then(function (Smile) {
        if (!self.isConnected) return;
        if (!Smile) {
          self.showBalanceFallback();
          return;
        }
        safeGet(Smile.pointsProducts).then(function (products) { self.applyPointsProducts(products); });
        safeGet(Smile.vipTiers).then(function (tiers) { self.applyVipTiers(tiers); });
        if (self.dataset.loggedIn === 'true') self.loadCustomer(Smile);
      });
    }

    applyPointsProducts(products) {
      if (!Array.isArray(products) || !products.length) return;
      var product = products.find(function (p) { return p && p.exchangeType === 'variable'; }) || products[0];
      var step = firstNumber(product.variablePointsStep, product.pointsPrice);
      var value = firstNumber(product.variablePointsStepRewardValue);
      if (step && step > 0) this.step = step;
      if (value && value > 0) this.reward = value;
      this.querySelectorAll('[data-zm-step]').forEach(function (el) { el.textContent = fmtPoints(this.step); }, this);
      this.querySelectorAll('[data-zm-reward]').forEach(function (el) { el.textContent = fmtMoney(this.reward); }, this);
      var terms = product.reward && product.reward.description;
      if (terms) {
        this.querySelectorAll('[data-zm-reward-terms]').forEach(function (el) { el.textContent = terms; });
      }
      this.renderSimulator();
      this.renderBalance();
    }

    applyVipTiers(tiers) {
      if (!Array.isArray(tiers) || tiers.length !== TIER_MILESTONES.length) return;
      var sorted = tiers.slice().sort(function (a, b) { return a.milestone - b.milestone; });
      this.vipTierIds = sorted.map(function (t) { return t.id; });
      this.milestones = sorted.map(function (t, i) {
        return typeof t.milestone === 'number' ? t.milestone : TIER_MILESTONES[i];
      });
      if (this.pendingTierId !== undefined) this.renderTier(this.pendingTierId);
    }

    loadCustomer(Smile) {
      var self = this;
      var customerPromise = Smile.customer && typeof Smile.customer.current === 'function'
        ? withTimeout(Smile.customer.current())
        : Promise.resolve(null);

      Promise.all([customerPromise, safeGet(Smile.customerPointsWallet), safeGet(Smile.customerVipStatus)])
        .then(function (results) {
          if (!self.isConnected) return;
          var customer = results[0] || {};
          var wallet = results[1] || {};
          var vip = results[2] || {};
          var balance = firstNumber(
            customer.pointsBalance, customer.points_balance, wallet.balance, wallet.pointsBalance
          );
          if (balance === null) return;
          self.setBalance(balance);
          var tierId = vip.vipTierId || (vip.vipTier && vip.vipTier.id) ||
            (vip.currentVipTier && vip.currentVipTier.id) || customer.vipTierId || null;
          self.pendingTierId = tierId;
          self.renderTier(tierId);
        });
    }

    /* Smile UI writes the balance into .sweettooth-points-balance on its own,
       so the card works even when the SDK objects never answer. */
    watchSmileUiBalance() {
      var el = this.querySelector('[data-zm-balance]');
      if (!el) return;
      var self = this;
      var read = function () {
        var n = parseInt(String(el.textContent).replace(/\D/g, ''), 10);
        if (!isNaN(n)) self.setBalance(n, true);
      };
      var observer = new MutationObserver(read);
      observer.observe(el, { childList: true, characterData: true, subtree: true });
      this.cleanups.push(function () { observer.disconnect(); });
      read();
      setTimeout(function () { if (self.balance === null) self.showBalanceFallback(); }, SMILE_WAIT_MS);
    }

    setBalance(balance, fromDom) {
      this.balance = balance;
      var el = this.querySelector('[data-zm-balance]');
      if (el && !fromDom) el.textContent = fmtPoints(balance);
      this.renderBalance();
    }

    renderBalance() {
      if (this.balance === null) return;
      var balance = this.balance;
      var coupons = Math.floor(balance / this.step);
      var into = balance % this.step;
      var missing = this.step - into;
      var value = this.querySelector('[data-zm-value]');
      if (value) {
        value.innerHTML = '';
        var b = document.createElement('b');
        if (coupons > 0) {
          b.textContent = fmtMoney(coupons * this.reward);
          value.append('Você já pode trocar ', b, ' em desconto.');
        } else {
          b.textContent = fmtPoints(missing) + ' pontos';
          value.append('Faltam ', b, ' para seu cupom de ' + fmtMoney(this.reward) + '.');
        }
      }
      var fill = this.querySelector('[data-zm-fill]');
      if (fill) fill.style.width = (coupons > 0 ? 100 : (into / this.step) * 100) + '%';
      var s1 = this.querySelector('[data-zm-s1]');
      if (s1) s1.textContent = fmtPoints(this.step) + ' pts = ' + fmtMoney(this.reward);
      var redeem = this.querySelector('[data-zm-redeem]');
      if (redeem) {
        redeem.textContent = coupons > 0
          ? 'Trocar ' + fmtPoints(this.step) + ' pontos por ' + fmtMoney(this.reward)
          : 'Ver minhas recompensas';
      }
    }

    renderTier(tierId) {
      var index = -1;
      if (tierId && this.vipTierIds) index = this.vipTierIds.indexOf(tierId);
      if (index < 0) return;
      var levels = this.querySelector('[data-zm-levels]');
      if (levels) {
        levels.hidden = false;
        var name = levels.querySelector('[data-zm-tier-name]');
        if (name) name.textContent = TIER_NAMES[index];
        var milestones = this.milestones;
        var balance = this.balance || 0;
        levels.querySelectorAll('[data-zm-lv]').forEach(function (li) {
          var i = parseInt(li.dataset.zmLv, 10);
          li.classList.toggle('now', i === index);
          var bar = li.querySelector('i span');
          if (!bar) return;
          var pct = i < index ? 100 : 0;
          if (i === index) {
            var next = milestones[i + 1];
            pct = next ? Math.min(100, Math.max(8, ((balance - milestones[i]) / (next - milestones[i])) * 100)) : 100;
          }
          bar.style.width = pct + '%';
        });
      }
      this.querySelectorAll('[data-zm-tier]').forEach(function (card) {
        var here = parseInt(card.dataset.zmTier, 10) === index;
        card.classList.toggle('is-here', here);
        var badge = card.querySelector('.zm-tier__here');
        if (badge) badge.hidden = !here;
      });
    }

    showBalanceFallback() {
      if (this.dataset.loggedIn !== 'true' || this.balance !== null) return;
      var note = this.querySelector('[data-zm-fallback]');
      var value = this.querySelector('[data-zm-value]');
      if (note) note.hidden = false;
      if (value) value.hidden = true;
    }

    /* ---------- Smile panel buttons ---------- */

    setupSmileButtons() {
      this.on(this, 'click', function (event) {
        var button = event.target.closest('[data-zm-open]');
        if (!button) return;
        var link = button.dataset.zmOpen;
        if (window.SmileUI && typeof window.SmileUI.openPanel === 'function') {
          window.SmileUI.openPanel({ deep_link: link });
        } else {
          window.location.hash = 'smile-' + link.replace(/_/g, '-');
        }
      });
    }

    /* ---------- simulator ---------- */

    setupSimulator() {
      this.simInput = this.querySelector('[data-zm-sim]');
      if (!this.simInput) return;
      var self = this;
      this.on(this.simInput, 'input', function () { self.renderSimulator(); });
      this.querySelectorAll('.zm-sim__quick button').forEach(function (button) {
        self.on(button, 'click', function () {
          self.simInput.value = button.dataset.v;
          self.renderSimulator();
        });
      });
      this.renderSimulator();
    }

    renderSimulator() {
      if (!this.simInput) return;
      var amount = parseInt(this.simInput.value, 10) || 0;
      var points = amount;
      var coupons = Math.floor(points / this.step);
      var into = points % this.step;
      var out = this.querySelector('[data-zm-sim-out]');
      if (out) out.textContent = fmtMoney(amount);
      var pts = this.querySelector('[data-zm-sim-pts]');
      if (pts) pts.textContent = fmtPoints(points);
      var fill = this.querySelector('[data-zm-sim-fill]');
      if (fill) fill.style.width = (coupons > 0 ? 100 : (into / this.step) * 100) + '%';
      var money = this.querySelector('[data-zm-sim-money]');
      if (money) {
        money.innerHTML = '';
        var b = document.createElement('b');
        if (coupons > 0) {
          b.textContent = fmtMoney(coupons * this.reward) + ' em desconto';
          money.append('Isso vale ', b, ' para a próxima compra.');
        } else {
          b.textContent = fmtPoints(this.step - into) + ' pontos';
          money.append('Faltam ', b, ' para um cupom de ' + fmtMoney(this.reward) + '.');
        }
      }
      this.querySelectorAll('.zm-sim__quick button').forEach(function (button) {
        button.setAttribute('aria-pressed', String(parseInt(button.dataset.v, 10) === amount));
      });
    }

    /* ---------- sticky anchors under the theme header ---------- */

    setupAnchor() {
      var nav = this.querySelector('[data-zm-anchor]');
      if (!nav) return;
      var self = this;
      var links = Array.prototype.slice.call(nav.querySelectorAll('a[data-spy]'));
      var header = document.querySelector('.section-header, .shopify-section-group-header-group.section-header, sticky-header');
      var ticking = false;

      var update = function () {
        ticking = false;
        var top = 0;
        if (header) top = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
        self.style.setProperty('--zm-sticky-top', top + 'px');
        var line = top + nav.offsetHeight + 24;
        var current = null;
        links.forEach(function (a) {
          var target = self.querySelector('#' + a.dataset.spy);
          if (target && target.getBoundingClientRect().top <= line) current = a;
        });
        links.forEach(function (a) {
          if (a === current) a.setAttribute('aria-current', 'true');
          else a.removeAttribute('aria-current');
        });
      };
      var onScroll = function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };
      this.on(window, 'scroll', onScroll, { passive: true });
      this.on(window, 'resize', onScroll);
      update();
    }

    /* ---------- tier carousel dots (mobile) ---------- */

    setupTierDots() {
      var rail = this.querySelector('[data-zm-tiers]');
      var dots = this.querySelectorAll('[data-zm-dots] span');
      if (!rail || !dots.length) return;
      this.on(rail, 'scroll', function () {
        var max = rail.scrollWidth - rail.clientWidth;
        var active = max > 0 ? Math.round((rail.scrollLeft / max) * (dots.length - 1)) : 0;
        dots.forEach(function (dot, i) { dot.classList.toggle('on', i === active); });
      }, { passive: true });
    }
  }

  customElements.define('zone-membership', ZoneMembership);
})();
